// src/pages/ProblemSystem.js
import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faInfo } from "@fortawesome/free-solid-svg-icons";
import {
  Breadcrumb,
  Card,
  Table,
  Badge,
  Button,
  Nav,
} from "@themesberg/react-bootstrap";

// ===== Badge สถานะ (ตามแท็บ) =====
const StatusBadge = ({ state }) => {
  if (state === "NEW") return <Badge bg="secondary">รายการใหม่</Badge>;
  if (state === "IN_PROGRESS")
    return <Badge bg="warning">กำลังดำเนินการ</Badge>;
  return <Badge bg="success">เสร็จสิ้น</Badge>;
};

// ===== MOCK DATA ให้ตรงคอลัมน์ใหม่ =====
// state: NEW, IN_PROGRESS, DONE
const INITIAL_TICKETS = [
  {
    title: "หน้าแรกไม่อัปเดต",
    detail:
      "หน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดตหน้าแรกไม่อัปเดต",
    date: "1 ต.ค. 2568",
    time: "11:00",
    reporter: "ผู้ใช้ 1",
    state: "NEW",
  },
];

// ===== ตาราง (ใช้กับทุกแท็บ) =====
const IssuesTable = ({ rows }) => {
  const RowItem = ({ idx, r }) => (
    <tr>
      <td
        style={{
          width: 50,
        }}
      >
        {idx}
      </td>
      <td
        style={{
          width: 50,
        }}
      >
        {r.title}
      </td>
      <td
        className="text-wrap"
        style={{
          width: 300,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
        title={r.detail}
      >
        {r.detail}
      </td>
      <td
        style={{
          width: 50,
        }}
      >
        {r.date}
      </td>
      <td
        style={{
          width: 50,
        }}
      >
        {r.time}
      </td>
      <td>{r.reporter}</td>
      <td className="text-center">
        <StatusBadge state={r.state} />
      </td>
      <td className="text-center">
        <Button variant="info" size="sm" className="me-2">
          อัพเดต
        </Button>
        <Button variant="danger" size="sm">
          ลบ
        </Button>
      </td>
    </tr>
  );

  return (
    <Card border="light" className="shadow-sm mb-4">
      <Card.Body className="pb-0">
        <Table responsive className="table-centered table-nowrap rounded mb-0">
          <thead className="thead-light">
            <tr>
              <th className="border-0">#</th>
              <th className="border-0">หัวข้อ</th>
              <th className="border-0">รายละเอียด</th>
              <th className="border-0">วันที่แจ้ง</th>
              <th className="border-0">เวลา</th>
              <th className="border-0 text-start">ผู้แจ้ง</th>
              <th className="border-0 text-center">สถานะ</th>
              <th className="border-0 text-center">จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((r, i) => <RowItem key={r.code} idx={i + 1} r={r} />)
            ) : (
              <tr>
                <td colSpan={10} className="text-center text-muted py-5">
                  ไม่มีข้อมูลในแท็บนี้
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

// ===== หน้าหลัก + แท็บ =====
export default function ProblemSystem() {
  const [tickets] = useState(INITIAL_TICKETS);
  const [activeTab, setActiveTab] = useState("NEW");

  const counts = useMemo(
    () => ({
      NEW: tickets.filter((t) => t.state === "NEW").length,
      IN_PROGRESS: tickets.filter((t) => t.state === "IN_PROGRESS").length,
      DONE: tickets.filter((t) => t.state === "DONE").length,
    }),
    [tickets]
  );

  const filtered = useMemo(
    () => tickets.filter((t) => t.state === activeTab),
    [tickets, activeTab]
  );

  return (
    <>
      {/* header */}
      <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-xl-0">
          <Breadcrumb
            className="d-none d-md-inline-block"
            listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}
          />
          <h4>รายงานการแจ้งปัญหา</h4>
        </div>
      </div>

      {/* tabs */}
      <Nav
        variant="pills"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "NEW")}
        className="mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="NEW">
            รายการใหม่{" "}
            <Badge bg="secondary" className="ms-1">
              {counts.NEW}
            </Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="IN_PROGRESS">
            กำลังดำเนินการ{" "}
            <Badge bg="secondary" className="ms-1">
              {counts.IN_PROGRESS}
            </Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="DONE">
            เสร็จสิ้น{" "}
            <Badge bg="secondary" className="ms-1">
              {counts.DONE}
            </Badge>
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* table by tab */}
      <IssuesTable rows={filtered} />
    </>
  );
}
